import { type RankKey, type RunResult, betterRank } from '../engine';
import { t } from '../i18n';

/**
 * 로컬 기록 — 키 'ajae.stats.v2', 전 접근 try/catch.
 * 서버가 없으므로 비교 대상은 자기 최고기록과 도전장 상대 둘뿐이다.
 * 스트릭·데일리는 저장하지 않는다 — 카톡 인앱 WebView 저장소 파티션에서 구조적으로 끊긴다(UPLIFT §0.2).
 * v1(점수 기반)은 SEAL 로 점수가 폐지되면서 버린다 — 읽으면 빈 통계로 취급한다.
 */
export interface BestRun extends RankKey {
  readonly ticks: number;
  readonly sealMs: number; // 봉인 성공 시 마지막 정답까지의 명목 ms, 실패면 -1
  readonly nominalMs: number; // 판 전체 명목 시간 — 벽시계가 아니라 틱의 함수라 결정적이다
  readonly sessionSeed: string; // (sessionSeed, runIndex) 쌍이어야 판이 복원된다
  readonly runIndex: number;
}

export interface LocalStats {
  readonly v: 2;
  readonly runs: number;
  readonly clears: number; // 봉인 성공 횟수
  readonly best: BestRun | null;
}

export const EMPTY_STATS: LocalStats = { v: 2, runs: 0, clears: 0, best: null };

const KEY = 'ajae.stats.v2';

/** 팩별로 기록을 나눈다 — 아재개그 최고와 테마 팩 최고는 다른 판이다. core 는 옛 키 그대로(P1 기록 보존). */
export function statsKey(packId = 'core'): string {
  return packId === 'core' ? KEY : `${KEY}.${packId}`;
}

/** 순위는 엔진의 단일 정렬식(SEAL RANK_KEY)을 그대로 쓴다. 동점은 '더 낫다'가 아니다(신기록이 아니다). */
export function betterRun(a: BestRun, b: BestRun): boolean {
  return betterRank(a, b);
}

export function toBestRun(r: RunResult): BestRun {
  return {
    sealed: r.sealed,
    solved: r.solved,
    lastSolveTick: r.lastSolveTick,
    endReason: r.endReason,
    ticks: r.ticks,
    // nominalMs 는 틱의 상수배이므로 비율이 곧 틱당 명목 ms 다
    sealMs: r.sealed && r.ticks > 0 ? Math.round((r.sealTicks * r.nominalMs) / r.ticks) : -1,
    nominalMs: r.nominalMs,
    sessionSeed: r.sessionSeed,
    runIndex: r.runIndex,
  };
}

/** 순수. quit/abandoned 로 닫힌 판은 호출부가 걸러낸다. */
export function commitRun(prev: LocalStats, r: RunResult): { stats: LocalStats; newBest: boolean } {
  const cand = toBestRun(r);
  // 첫 판은 '신기록'이 아니다 — 원안대로면 첫 판이 항상 "신기록! 이전 0"이 되어 즉시 프로토 냄새가 난다
  const newBest = prev.best !== null && betterRun(cand, prev.best);
  const best = prev.best === null || newBest ? cand : prev.best;
  return {
    stats: {
      v: 2,
      runs: prev.runs + 1,
      clears: prev.clears + (cand.sealed ? 1 : 0),
      best,
    },
    newBest,
  };
}

function isBestRun(x: unknown): x is BestRun {
  if (typeof x !== 'object' || x === null) return false;
  const b = x as Record<string, unknown>;
  return (
    typeof b.sealed === 'boolean' &&
    typeof b.solved === 'number' &&
    typeof b.lastSolveTick === 'number' &&
    typeof b.endReason === 'string' &&
    typeof b.ticks === 'number' &&
    typeof b.sealMs === 'number' &&
    typeof b.nominalMs === 'number' &&
    typeof b.sessionSeed === 'string' &&
    typeof b.runIndex === 'number'
  );
}

export function readStats(packId = 'core'): LocalStats {
  try {
    const raw = localStorage.getItem(statsKey(packId));
    if (raw === null) return EMPTY_STATS;
    const v = JSON.parse(raw) as Partial<LocalStats>;
    if (v.v !== 2) return EMPTY_STATS;
    return {
      v: 2,
      runs: typeof v.runs === 'number' ? v.runs : 0,
      clears: typeof v.clears === 'number' ? v.clears : 0,
      best: isBestRun(v.best) ? v.best : null,
    };
  } catch {
    return EMPTY_STATS;
  }
}

export function writeStats(s: LocalStats, packId = 'core'): boolean {
  try {
    localStorage.setItem(statsKey(packId), JSON.stringify(s));
    return true;
  } catch {
    return false;
  }
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000)); // dom.formatClock 과 같은 내림 — HUD 1:27 / 기록 1:28 불일치 방지
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** 타이틀·게임오버에 쓰는 한 줄 요약 */
export function describeBest(b: BestRun, runWordCount: number): string {
  return b.sealed
    ? t().best.sealed(formatClock(b.sealMs))
    : t().best.alive(b.solved, runWordCount, formatClock(b.nominalMs));
}
